/**
 * Service-worker update helpers.
 * Reload at most once per page lifetime when a new SW takes control,
 * so skipWaiting + clientsClaim cannot loop.
 */

export interface ReloadOnce {
  (): void;
  flush: () => void;
}

export function createReloadOnce(
  reload: () => void = () => {
    window.location.reload();
  },
  isBlocked: () => boolean = () => false,
  onBlocked?: (reload: () => void) => void
): ReloadOnce {
  let reloaded = false;
  let queued = false;

  const run = (): void => {
    if (reloaded) {
      return;
    }
    reloaded = true;
    reload();
  };

  const attempt = (): void => {
    if (reloaded) {
      return;
    }
    if (isBlocked()) {
      queued = true;
      onBlocked?.(run);
      return;
    }
    run();
  };

  const flush = (): void => {
    if (reloaded || !queued || isBlocked()) {
      return;
    }
    run();
  };

  return Object.assign(attempt, { flush });
}

export interface UpdateCheckOptions {
  addVisibilityListener?: (handler: () => void) => void;
  getVisibility?: () => DocumentVisibilityState;
}

/**
 * Check for a new service worker immediately (page load) and whenever
 * the tab becomes visible again (iOS PWAs often resume from the icon).
 */
export function bindUpdateChecks(
  update: () => void,
  options: UpdateCheckOptions = {}
): void {
  update();

  const addVisibilityListener =
    options.addVisibilityListener ??
    ((handler: () => void): void => {
      document.addEventListener('visibilitychange', handler);
    });
  const getVisibility =
    options.getVisibility ?? ((): DocumentVisibilityState => document.visibilityState);

  addVisibilityListener((): void => {
    if (getVisibility() === 'visible') {
      update();
    }
  });
}

/**
 * First SW install + clientsClaim fires controllerchange even though
 * this is not an update. Reloading then wipes in-memory route state
 * (Challenge / About you / Start). Only reload when a controller is
 * replaced — a real update.
 */
export function createControllerChangeHandler(
  hadController: boolean,
  reload: () => void
): () => void {
  let claimed = hadController;
  return (): void => {
    if (!claimed) {
      claimed = true;
      return;
    }
    reload();
  };
}
